import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import AppointmentListView from './AppointmentList.view';
import useGrouping from 'Common/Utils/Hooks/useGrouping';
import { fetchAppointmentsForTeamIds } from '../service';
import errorCb from 'Common/Utils/errorCb';
import groupings from 'Common/Constants/Groupings';
import { commonSchema, invigilatorSchema, verifierSchema, othersSchema } from './constants';
import { commonFields, invigilatorFields, verifierFields, othersFields } from './constants';

const defaultTitle = 'Appointment Management';

const AppointmentList = () => {
	const [year, updateYear] = useState('');
	const [appointments, setAppointments] = useState([]);
	const [refinedAppointments, setRefinedAppointments] = useState([]);
	const [displayAppointments, setDisplayAppointments] = useState([]);
	const [isFiltering, setIsFiltering] = useState(true);
	const [inPreview, setInPreview] = useState(false);
	const [filterOpen, setFilterOpen] = useState(true);
    const [filterParams, setFilterParams] = useState();
	const [refinerOpen, setRefinerOpen] = useState(false);
	const [updaterOpen, setUpdaterOpen] = useState(false);
	const [viewDataOpen, setViewDataOpen] = useState(false);
	const globalDispatch = useDispatch();
	const grouping = useGrouping();
    const appointmentSchema = useMemo(() => getAppointmentSchema(grouping), [grouping]);
    const refinerFields = useMemo(() => getRefinerFields(grouping), [grouping]);

    const toggleFilter = useCallback(() => setFilterOpen(prev => !prev), []);
	const toggleRefiner = useCallback(() => setRefinerOpen(prev => !prev), []);
	const toggleUpdater = useCallback(() => setUpdaterOpen(prev => !prev), []);
	const toggleViewData = useCallback(() => setViewDataOpen(prev => !prev), []);

	const startNewFilter = () => {
		setAppointments([]);
		setRefinedAppointments([]);
		setDisplayAppointments([]);
		setIsFiltering(true);
		setInPreview(false);
		setFilterOpen(true);
		setRefinerOpen(false);
		setUpdaterOpen(false);
		setViewDataOpen(false);
	};

    const loadAppointments = useCallback(async (selectedYear, filteredTypes, filteredTeamIds, onLoad) => {
        const onError = errorCb([], `Error fetching Appointments for ${grouping}`, globalDispatch);
        const data = await fetchAppointmentsForTeamIds(selectedYear, grouping, filteredTypes, filteredTeamIds, onError);
        data.name = "Filtered"
        setAppointments(data);
        setDisplayAppointments(data);
        setViewDataOpen(true);
        setIsFiltering(false);
        setFilterOpen(false);
        onLoad && onLoad();
    }, [grouping, globalDispatch]);

	function handleRefinementChange(data) {
		setRefinedAppointments(data);
		setDisplayAppointments(data);
	}

    const handleFilterSubmit = useCallback((selectedYear, filteredTypes, filteredTeamIds) => {
        setFilterParams({ selectedYear, filteredTypes, filteredTeamIds });
        loadAppointments(selectedYear, filteredTypes, filteredTeamIds, () => {
            setRefinerOpen(true);
        });
    }, [loadAppointments]);

    const handleFilterClear = useCallback(() => {
        setAppointments([]);
    }, []);

    const startNewUpdate = useCallback(() => {
        const { selectedYear, filteredTypes, filteredTeamIds } = filterParams;
        loadAppointments(selectedYear, filteredTypes, filteredTeamIds);
    }, [loadAppointments, filterParams]);

    const displayTitle = displayAppointments.name ? `${year} ${grouping} Appointments (${displayAppointments.name})` : defaultTitle;

	return (
		<AppointmentListView
            appointmentSchema={appointmentSchema}
            refinerFields={refinerFields}
			appointments={appointments}
			refinerOpen={refinerOpen}
			refinedAppointments={refinedAppointments}
			onRefinementChange={handleRefinementChange}
			toggleRefiner={toggleRefiner}
			displayAppointments={displayAppointments}
			filterOpen={filterOpen}
			toggleFilter={toggleFilter}
			isFiltering={isFiltering}
			setIsFiltering={setIsFiltering}
			year={year}
			updateYear={updateYear}
			updaterOpen={updaterOpen}
			toggleUpdater={toggleUpdater}
			grouping={grouping}
			inPreview={inPreview}
			setInPreview={setInPreview}
			setDisplayAppointments={setDisplayAppointments}
			displayTitle={displayTitle}
			viewDataOpen={viewDataOpen}
			setViewDataOpen={setViewDataOpen}
			startNewFilter={startNewFilter}
			toggleViewData={toggleViewData}
            handleFilterSubmit={handleFilterSubmit}
            handleFilterClear={handleFilterClear}
            startNewUpdate={startNewUpdate}
		/>
	);
};

function getAppointmentSchema(grouping) {
    switch (grouping) {
        case groupings.Invigilator:
            return [...commonSchema, ...invigilatorSchema];

        case groupings.Verifier:
            return [...commonSchema, ...verifierSchema];

        default:
            return [...commonSchema, ...othersSchema];
    }
}

function getRefinerFields(grouping) {
    switch (grouping) {
        case groupings.Invigilator:
            return [...commonFields, ...invigilatorFields];

        case groupings.Verifier:
            return [...commonFields, ...verifierFields];

        default:
            return [...commonFields, ...othersFields];
    }
}

export default AppointmentList;
